import { useCallback, useEffect, useState } from "react"
import { useDomainErrorsInSync } from "./helpers"
import { selectAccountGuid } from "../authentication/authenticationSlice"
import { useSelector } from "react-redux"
import {
  mainPracticeThunk,
  selectPracticeDataStatus,
} from "../../domain/Practice/loaders/loader"
import { useAppDispatch, useAppNavigate } from "../../reduxStore/hooks"
import {
  fetchBaseProvider,
  selectProviderDataStatus,
} from "../../domain/Provider/loader"
import {
  dataStatusesError,
  dataStatusesLoading,
  dataStatusesSending,
} from "../shared/utils"
import { selectStatus as getTouStatus } from "../termsOfUse/termsOfUseSlice"
import { appQueryParams, appRoutes } from "../../routes"
import { selectPractice } from "../../domain/Practice/model"
import { selectProvider } from "../../domain/Provider/model"
import { LoadStatus, useUserDomainState } from "./useUserDomainState"
import { selectPracticeInvites } from "../../domain/Practices/selectors"
import { selectPractices } from "../../domain/Practices/selectors"
import { selectIsUserOnboarded } from "../../domain/Practices/selectors"
import { AutoSelectedPracticeIdEnum } from "../../domain/Practices/helpers"
import { selectAutoSelectedPracticeResult } from "../../domain/Practices/selectors"
import { fetchReferralByIdThunk } from "../../domain/Referral/loader"
import { ReferralSummary } from "../../domain/Referral/schema"
import { getRouteWithQueryParams } from "../../routes"

export type NavigationState = {
  url: string
  waitForFetch: {
    practice: boolean
    provider: boolean
  }
}

/**
 * Custom hook for determining the needed initial load and steady state ( post-initial load)
 * navigation based off current UserDomainState. Additionally, this handles query param stripping on error.
 * The output to this orchestrator is a url that is consumed by the DataFetchingRouter.ts
 * @returns {string}
 */
export const useNavigationOrchestrator = (
  readyToExecute: boolean,
  finalizedDeepLinkUrl?: string,
) => {
  const [currentNavState, setCurrentNavState] = useState<NavigationState>({
    url: "",
    waitForFetch: {
      practice: false,
      provider: false,
    },
  })
  const navigate = useAppNavigate()
  const dispatch = useAppDispatch()
  const userId = useSelector(selectAccountGuid)
  const isUserOnboarded = useSelector(selectIsUserOnboarded)
  const touStatus = useSelector(getTouStatus)
  const practiceInvites = useSelector(selectPracticeInvites)
  const practice = useSelector(selectPractice)
  const practices = useSelector(selectPractices) // partial practices corresponding to the users invites
  const provider = useSelector(selectProvider)
  const practiceDataStatus = useSelector(selectPracticeDataStatus)
  const providerDataStatus = useSelector(selectProviderDataStatus)
  // Pass all deep link values to useUserDomainState
  const { currentDomainState, setCurrentDomainState } = useUserDomainState(
    readyToExecute,
    finalizedDeepLinkUrl,
  )
  const domainErrorsInSyncSync = useDomainErrorsInSync(currentDomainState)
  const autoSelectedPracticeIdResult = useSelector(
    selectAutoSelectedPracticeResult,
  )

  const domainHasInitialDataStatus =
    practiceDataStatus.fullPracticeDataStatus === "loading" &&
    providerDataStatus.baseProviderDataStatus === "loading"

  const domainFetchSending = dataStatusesSending([
    practiceDataStatus.fullPracticeDataStatus,
    providerDataStatus.baseProviderDataStatus,
  ])
  const domainFetchError = dataStatusesError([
    practiceDataStatus.fullPracticeDataStatus,
    providerDataStatus.baseProviderDataStatus,
  ])
  const practiceLoadingOrSending = dataStatusesLoading([
    practiceDataStatus.fullPracticeDataStatus,
  ])
  const providerLoadingOrSending = dataStatusesLoading([
    providerDataStatus.baseProviderDataStatus,
  ])

  const waitingForDomainLoad = useCallback(() => {
    let waitingForDomainLoad = false
    if (currentNavState.waitForFetch.practice && practiceLoadingOrSending) {
      waitingForDomainLoad = true
    }
    if (currentNavState.waitForFetch.provider && providerLoadingOrSending) {
      waitingForDomainLoad = true
    }
    return waitingForDomainLoad
  }, [
    currentNavState.waitForFetch.practice,
    currentNavState.waitForFetch.provider,
    practiceLoadingOrSending,
    providerLoadingOrSending,
  ])

  // initial state fetch and nav
  useEffect(() => {
    if (
      currentDomainState.loadStatus === "transient" &&
      userId &&
      !domainFetchSending
    ) {
      const startingPath = window.location.pathname
      const startingHash = window.location.hash
      const isPublicStart = [appRoutes.HELP_ROUTE].includes(startingPath)
      // Debug logging
      const navRoute =
        currentDomainState.initialState.routeTo || appRoutes.INTERNAL_ERROR
      const urlParams = new URLSearchParams(navRoute.split("?")[1])

      const extractedPracticeId = urlParams.get(appQueryParams.PRACTICE_ID)
      const extractedProviderId = urlParams.get(appQueryParams.PROVIDER_ID)
      const extractedReferralPacketGuid = urlParams.get(
        appQueryParams.REFERRAL_PACKET_GUID,
      )

      // default the practice based off state/query params - query params superceeds state (deep link)
      // we only want to use the id if it is part of a practice that the user is already affiliated with (avoid 403 fetching)
      // additionally, do not fetch if the user is onboarded and has multiple practices
      const skipPracticeFetch = isUserOnboarded && practices.length > 1
      const defaultPracticeId =
        autoSelectedPracticeIdResult?.type ===
          AutoSelectedPracticeIdEnum.AFFILIATED && !skipPracticeFetch
          ? autoSelectedPracticeIdResult.practiceId
          : null
      const practiceIdToFetch = extractedPracticeId || defaultPracticeId

      const fetchPractice =
        !!practiceIdToFetch && practiceIdToFetch !== practice.id
      const fetchProvider =
        !!extractedProviderId && extractedProviderId !== provider.id
      const fetchReferralPacket = !!extractedReferralPacketGuid

      if (fetchPractice) {
        dispatch(
          mainPracticeThunk({
            userId,
            practiceId: practiceIdToFetch,
          }),
        )
      }
      if (fetchProvider) {
        dispatch(
          fetchBaseProvider({
            providerId: extractedProviderId,
          }),
        )
      }
      if (fetchReferralPacket && touStatus === "accepted") {
        // Fetch the referral by ID to get practice information
        dispatch(
          fetchReferralByIdThunk({
            referralId: extractedReferralPacketGuid,
          }),
        ).then((result) => {
          if (result.meta.requestStatus === "fulfilled") {
            // Navigate to referrals screen with practice ID
            const referralData = result.payload as ReferralSummary
            const practiceId = referralData.practiceId

            if (practiceId) {
              const referralsRoute = getRouteWithQueryParams(
                appRoutes.referrals.DETAILS_ROUTE,
                {
                  referralId: extractedReferralPacketGuid,
                  practiceId: practiceId,
                },
              )
              setCurrentNavState({
                url: referralsRoute,
                waitForFetch: {
                  practice: false,
                  provider: false,
                },
              })
            }
          }
        })
      } else {
        // Only set the default navigation state if we're not fetching a referral packet
        let targetUrl =
          currentDomainState.initialState.routeTo ||
          appRoutes.taskboard.INDEX_ROUTE
        // Preserve hash if starting on a public help page and targetUrl equals startingPath
        if (isPublicStart) {
          try {
            const targetPath = targetUrl.split("?")[0]
            if (targetPath === startingPath && startingHash) {
              targetUrl = targetUrl + startingHash
            }
          } catch {}
        }
        setCurrentNavState({
          url: targetUrl,
          waitForFetch: {
            practice: fetchPractice,
            provider: fetchProvider,
          },
        })
      }
    }
  }, [
    autoSelectedPracticeIdResult,
    currentDomainState,
    dispatch,
    domainFetchSending,
    domainHasInitialDataStatus,
    isUserOnboarded,
    practice.id,
    practiceInvites,
    practices,
    provider.id,
    touStatus,
    userId,
  ])

  // useEffect to set currentDomainState.loadStatus from "transient" to "steady"
  // after initial url has been set and the domain fetches are done sending (if needed)
  useEffect(() => {
    if (
      currentDomainState.loadStatus === "transient" &&
      !!currentNavState.url &&
      !waitingForDomainLoad()
    ) {
      setCurrentDomainState((prevState) => {
        const newState = {
          ...prevState,
          loadStatus: "steady" as LoadStatus,
        }
        return newState
      })
      navigate(currentNavState.url)
    }
  }, [
    currentDomainState.loadStatus,
    currentNavState.url,
    navigate,
    setCurrentDomainState,
    waitingForDomainLoad,
  ])

  // post-intial-load navigation for practice
  useEffect(() => {
    if (
      currentDomainState.loadStatus === "steady" &&
      currentDomainState.steadyState.refetchPractice &&
      domainErrorsInSyncSync &&
      !!currentDomainState.steadyState.newPracticeId &&
      !!userId &&
      practiceDataStatus.fullPracticeDataStatus !== "sending"
    ) {
      dispatch(
        mainPracticeThunk({
          userId,
          practiceId: currentDomainState.steadyState.newPracticeId,
        }),
      )
    }
  }, [
    currentDomainState,
    dispatch,
    domainErrorsInSyncSync,
    domainHasInitialDataStatus,
    practiceDataStatus.fullPracticeDataStatus,
    userId,
  ])

  // post-intial-load navigation for provider
  useEffect(() => {
    if (
      currentDomainState.loadStatus === "steady" &&
      currentDomainState.steadyState.refetchProvider &&
      domainErrorsInSyncSync &&
      !!currentDomainState.steadyState.newProviderId &&
      !!userId &&
      providerDataStatus.baseProviderDataStatus !== "sending"
    ) {
      dispatch(
        fetchBaseProvider({
          providerId: currentDomainState.steadyState.newProviderId,
        }),
      )
    }
  }, [
    currentDomainState,
    dispatch,
    domainErrorsInSyncSync,
    domainHasInitialDataStatus,
    providerDataStatus.baseProviderDataStatus,
    userId,
  ])

  // handle fetching error
  // strip the query params and nav to whatever route was requested
  useEffect(() => {
    if (
      currentDomainState.loadStatus === "steady" &&
      !!currentDomainState.fetchingError &&
      domainErrorsInSyncSync &&
      !domainFetchSending &&
      domainFetchError &&
      currentDomainState.stripQueryParams
    ) {
      setCurrentNavState({
        url: location.pathname,
        waitForFetch: {
          practice: false,
          provider: false,
        },
      })
    }
  }, [
    currentDomainState,
    dispatch,
    domainErrorsInSyncSync,
    domainFetchError,
    domainFetchSending,
    domainHasInitialDataStatus,
    userId,
  ])

  return {
    ...currentNavState,
    complete: currentDomainState.loadStatus === "steady",
  }
}
